import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { handleLogin } from '../../common/auth/AuthService';
import {
  Stack,
  Button,
  IconButton,
  TextField,
  FormControl,
  Typography,
  Input,
  InputAdornment,
  InputLabel,
  FormHelperText,
  Link as MaterialLink,
  Box,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material/';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { AUTHROOT, RESETPASS, SIGNUP } from '../../routes/CONSTANTS';
import PageTitle from '../../common/components/PageTitle';
import LoginIcon from '@mui/icons-material/Login';
import { Auth } from 'aws-amplify';
import { showWarningSnackbar } from '../../common/snackbar/SnackbarActions';

const validationSchema = Yup.object().shape({
  username: Yup.string().email('Invalid Email').required('Required'),
  password: Yup.string().required('Required'),
});

export function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from?.pathname || AUTHROOT;

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        if (user) {
          navigate(from);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <div>
      <PageTitle title='Member Login' />
      <Formik
        initialValues={{ username: '', password: '' }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleLogin(values)
            .then(() => {
              navigate(from, { replace: true });
            })
            .catch((err) => {
              if (err.code === 'UserNotConfirmedException') {
                showWarningSnackbar(
                  'Your account is not confirmed, please confirm your account to continue'
                );
                navigate(SIGNUP, {
                  state: {
                    activeStep: 1,
                    email: values.username,
                    password: values.password,
                  },
                });
              } else {
                console.error(err);
                setErrorMessage(err.message);
                setSubmitting(false);
              }
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Stack>
            <Form>
              <div>
                <TextField
                  name='username'
                  type='email'
                  label='Email'
                  autoComplete='username'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.username}
                  placeholder='Email'
                  variant='standard'
                  error={errors.username && touched.username}
                  helperText={touched.username && errors.username}
                  sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                  inputProps={{ maxLength: 256 }}
                />
              </div>
              <div>
                <FormControl
                  sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                  variant='standard'
                >
                  <InputLabel htmlFor='standard-adornment-password'>
                    Password
                  </InputLabel>
                  <Input
                    name='password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    placeholder='Password'
                    variant='standard'
                    error={errors.password && touched.password}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          sx={{ m: 0 }}
                          aria-label='toggle password visibility'
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge='end'
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    inputProps={{ maxLength: 64 }}
                  />
                  <FormHelperText id='password-error-message' error>
                    {touched.password && errors.password}
                  </FormHelperText>
                </FormControl>
              </div>
              <div>
                {errorMessage && (
                  <Typography sx={{ color: 'error.main' }}>
                    {errorMessage}
                  </Typography>
                )}
                <Box sx={{ m: 1, position: 'relative' }}>
                  <Button
                    sx={{ marginTop: 1 }}
                    type={'submit'}
                    disabled={isSubmitting}
                    variant='contained'
                  >
                    <LoginIcon style={{ marginRight: '5' }} />
                    Login
                  </Button>
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: 'secondary.main',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-6px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              </div>
            </Form>
            <div>
              <br />
              <br />
              <Typography>
                <MaterialLink component={RouterLink} to={RESETPASS}>
                  Forgot Password?
                </MaterialLink>
              </Typography>
            </div>
            <div>
              <br />
              <Typography>
                Don't have an account?{' '}
                <MaterialLink component={RouterLink} to={SIGNUP}>
                  Click here to start a FREE trial!
                </MaterialLink>
              </Typography>
            </div>
          </Stack>
        )}
      </Formik>
    </div>
  );
}
